import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { IndianRupee } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { fmt, today } from "../utils";
import { useLang } from "../i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const modes = ["NEFT", "RTGS", "UPI", "Cheque", "Cash"];

export default function Payments() {
  const [ledger, setLedger] = useState(null);
  const [tab, setTab] = useState("receivable");
  const [payModal, setPayModal] = useState(null);
  const [pay, setPay] = useState({ date: today(), amount: "", mode: "NEFT", note: "" });
  const { t } = useLang();

  const load = async () => setLedger((await api.get("/ledger")).data);
  useEffect(() => { load().catch(() => {}); }, []);

  const submitPayment = async () => {
    const amount = Number(pay.amount);
    if (!amount || amount <= 0) return toast.error("Amount daalo");
    if (payModal?.bill?.due != null && amount - Number(payModal.bill.due) > 0.005) {
      return toast.error(`Payment outstanding se zyada nahi ho sakta (max ${fmt(payModal.bill.due)})`);
    }
    try {
      await api.post(`/${payModal.kind}/${payModal.bill.id}/payments`, { ...pay, amount });
      toast.success("Payment record ho gaya");
      setPayModal(null); setPay({ date: today(), amount: "", mode: "NEFT", note: "" }); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Payment save nahi hua");
    }
  };

  const groups = ledger ? (tab === "receivable" ? ledger.receivables : ledger.payables) : [];
  const totalDue = groups.reduce((s, g) => s + g.due, 0);

  return (
    <div data-testid="payments-page" className="sgp-page">
      <div>
        <h1 className="sgp-page-title">{t("payments")}</h1>
        <p className="sgp-page-subtitle">Company-wise payable aur hospital-wise receivable — partial payments bhi track hote hain.</p>
      </div>

      <div className="sgp-toolbar">
        <button data-testid="tab-receivable" onClick={() => setTab("receivable")}
          className={`text-xs font-semibold px-4 py-2 rounded-full border transition-colors ${tab === "receivable" ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300 text-slate-600"}`}>
          Receivable (Hospitals se lena)
        </button>
        <button data-testid="tab-payable" onClick={() => setTab("payable")}
          className={`text-xs font-semibold px-4 py-2 rounded-full border transition-colors ${tab === "payable" ? "bg-rose-600 border-rose-600 text-white" : "border-slate-300 text-slate-600"}`}>
          Payable (Companies ko dena)
        </button>
        <div data-testid="total-due" className="ml-auto rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm">
          <span className="text-xs text-slate-500 uppercase tracking-wider">Total Due: </span>
          <span className="font-mono font-bold text-slate-900">{fmt(totalDue)}</span>
        </div>
      </div>

      {!ledger ? <p className="text-sm text-slate-400">Loading...</p> : (
        <div className="space-y-4" data-testid="ledger-list">
          {groups.map((g) => (
            <div key={g.party} className="sgp-card p-5">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <h3 className="font-heading font-bold text-slate-800">{g.party}</h3>
                <div className="flex gap-5 text-xs font-mono">
                  <span className="text-slate-500">Net Billed: <b className="text-slate-800">{fmt(g.billed)}</b></span>
                  <span className="text-slate-500">Paid: <b className="text-emerald-700">{fmt(g.paid)}</b></span>
                  <span className="text-slate-500">Due: <b className={g.due > 0 ? "text-rose-600" : "text-slate-400"}>{fmt(g.due)}</b></span>
                  {g.credit_balance > 0 && <span className="text-slate-500">Credit: <b className="text-amber-700">{fmt(g.credit_balance)}</b></span>}
                </div>
              </div>
              <table className="w-full">
                <thead>
                  <tr><th className="sgp-th">Bill</th><th className="sgp-th">Date</th><th className="sgp-th text-right">Net Total</th>
                    <th className="sgp-th text-right">Returned</th><th className="sgp-th text-right">Paid</th>
                    <th className="sgp-th text-right">Due</th><th className="sgp-th"></th></tr>
                </thead>
                <tbody>
                  {g.bills.map((b) => (
                    <tr key={b.id}>
                      <td className="sgp-td font-mono text-xs">
                        <Link target="_blank" rel="noreferrer"
                          to={`/print/${tab === "receivable" ? "sale" : "purchase"}/${b.id}`}
                          className="font-semibold text-teal-700 hover:underline">{b.bill_no}</Link>
                      </td>
                      <td className="sgp-td font-mono text-xs">{b.date}</td>
                      <td className="sgp-td text-right font-mono text-xs">{fmt(b.total)}</td>
                      <td className="sgp-td text-right font-mono text-xs text-amber-700">{b.returned_total > 0 ? fmt(b.returned_total) : "—"}</td>
                      <td className="sgp-td text-right font-mono text-xs text-emerald-700">{fmt(b.paid)}</td>
                      <td className="sgp-td text-right font-mono text-xs">
                        <span className={b.due > 0 ? "text-rose-600 font-semibold" : "text-slate-400"}>{fmt(b.due)}</span>
                      </td>
                      <td className="sgp-td text-right">
                        {b.due > 0 && (
                          <button data-testid={`record-payment-${b.id}`}
                            onClick={() => { setPayModal({ kind: tab === "receivable" ? "sales" : "purchases", bill: b, party: g.party }); setPay({ ...pay, amount: b.due }); }}
                            className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-teal-50 text-teal-700 hover:bg-teal-100 flex items-center gap-1 ml-auto">
                            <IndianRupee className="h-3 w-3" /> Record Payment
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {!groups.length && <div className="sgp-card p-10 text-center text-sm text-slate-400">Abhi koi {tab === "receivable" ? "receivable" : "payable"} nahi — bills banne ke baad yahan dikhenge.</div>}
        </div>
      )}

      {!!ledger?.tender_securities?.length && <div className="sgp-card p-5"><h2 className="font-heading font-bold mb-3">Tender EMD / security references</h2><p className="text-xs text-slate-500 mb-3">Tracked separately from invoice receivables and GST totals.</p><div className="divide-y">{ledger.tender_securities.map(s => <div key={s.id} className="py-2 text-sm flex justify-between"><span>{s.kind} · {s.number || s.payment_reference || s.tender_id}</span><span>{fmt(s.amount)} · {s.status}</span></div>)}</div></div>}

      <Dialog open={!!payModal} onOpenChange={() => setPayModal(null)}>
        <DialogContent data-testid="payment-modal" className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Payment — {payModal?.bill?.bill_no}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">{payModal?.party} · Due: <span className="font-mono font-semibold text-rose-600">{fmt(payModal?.bill?.due)}</span></p>
          <div className="space-y-3 mt-1">
            <div><label className="sgp-label">Amount (₹)</label>
              <input data-testid="payment-amount-input" type="number" step="0.01" min="0.01" max={payModal?.bill?.due || undefined} className="sgp-input font-mono" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} /></div>
            <div><label className="sgp-label">Date</label>
              <input data-testid="payment-date-input" type="date" className="sgp-input font-mono" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} /></div>
            <div><label className="sgp-label">Mode</label>
              <select data-testid="payment-mode-input" className="sgp-input" value={pay.mode} onChange={(e) => setPay({ ...pay, mode: e.target.value })}>
                {modes.map((m) => <option key={m}>{m}</option>)}
              </select></div>
            <div><label className="sgp-label">Note</label>
              <input data-testid="payment-note-input" className="sgp-input" value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} /></div>
            <button data-testid="payment-save-btn" onClick={submitPayment}
              className="w-full py-2 text-sm font-semibold rounded-md bg-teal-700 text-white hover:bg-teal-800">Save Payment</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
